import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { v4 as uuid } from 'uuid';
import { Box, Checkbox, FormControlLabel, FormHelperText, MenuItem, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { ConfigFieldControl, ConfigInput, ConfigSortableList, ConfigTextField, CoreEvent, useDebounce, useLocalCache } from '@sierralabs/core-ui';
import { mergeExpressionMaps } from '@sierralabs/query-core';
import { applyQueryFilters, useQueryContext } from '@sierralabs/query-ui';
import { TaskEvent, TaskEventType } from '@sierralabs/workflow-ui';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import NestedComponentPicker from '../../common/pickers/NestedComponentPicker';
import SiteQueryFieldPicker from '../../common/pickers/SiteQueryFieldPicker';
import SiteTableRenderer from '../../common/table/SiteTableRenderer';
import SiteConfigComponentQuery from '../../config/SiteConfigComponentQuery';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteEvent } from '../../hooks/event/useSiteEvent';
import { useSiteTablePagination } from '../../hooks/site-table/useSiteTablePagination';
import { useRouteLocalCacheKey } from '../../hooks/site-route/useRouteLocalCacheKey';
import { useSiteContext } from '../../SiteContext';
import SiteEventConfigurator from '../events/SiteEventConfigurator';
import { SiteComponent } from '../SiteComponentPlugin';
import { AssignEntityEvent, useAssignEntityComponent, useAssignEntityDisabled } from './AssignEntityPlugin';
export function createSiteTableFilterExpressionMap(
// filter field
field, 
// routeParam value from params
filterTarget = '') {
    return {
        queryType: 'select',
        wheres: [
            {
                type: 'simple',
                condition: { operator: 'equal', parameters: [field, filterTarget] }
            }
        ]
    };
}
const StyledConfigFieldControl = styled(ConfigFieldControl) `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
export const SiteTable = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { children } = props;
    const [search] = useQueryParam('search', withDefault(StringParam, ''));
    const debouncedSearch = useDebounce(search, 300);
    const component = useSubscribeComponentChanges(SiteTablePlugin, props);
    // console.log('SiteTable component', component);
    const { editing, isHover, hoverProps, setHoverComponent } = useSiteContext();
    const { queryResults, runQueryOrPaginated } = useQueryContext();
    const assignEntity = useAssignEntityComponent(component);
    const { disabled } = useAssignEntityDisabled(assignEntity);
    const params = useParams();
    const siteEvent = useSiteEvent(component);
    const { pagination, setPagination } = useSiteTablePagination({
        asQueryParams: (_a = component.props) === null || _a === void 0 ? void 0 : _a.sortWithQueryParams
    });
    const { searchFields } = component.props || {};
    // console.log('site table queryResults', depth, queryResults);
    const isHovering = useMemo(() => {
        return isHover(component);
    }, [component, isHover]);
    const className = useMemo(() => {
        const classes = ['site-table'];
        isHovering && classes.push('component-hover');
        return classes.join(' ');
    }, [isHovering]);
    // TODO: probably need to restrict table to only have one query. Not sure
    // how we will handle traversing multiple queries for table rows display.
    const queryId = (_b = component.queryIds) === null || _b === void 0 ? void 0 : _b[0];
    const queryResult = queryResults.find(qr => qr.queryId === queryId);
    const hidePagination = (_c = component.props) === null || _c === void 0 ? void 0 : _c.hidePagination;
    const filter = (_d = component.props) === null || _d === void 0 ? void 0 : _d.filter;
    const filterTarget = (filter === null || filter === void 0 ? void 0 : filter.routeParam)
        ? params[filter === null || filter === void 0 ? void 0 : filter.routeParam]
        : undefined;
    // get query filters from cache
    const localCacheKey = useRouteLocalCacheKey(component, { queryId });
    const { value: cachedQuery } = useLocalCache(localCacheKey, undefined);
    const expressionMap = useMemo(() => {
        const expressionMap = { queryType: 'select' };
        if (filter) {
            const filterExpressionMap = filter &&
                createSiteTableFilterExpressionMap(filter.field || '', filterTarget);
            expressionMap.wheres = (expressionMap.wheres || []).concat((filterExpressionMap === null || filterExpressionMap === void 0 ? void 0 : filterExpressionMap.wheres) || []);
        }
        const config = (cachedQuery === null || cachedQuery === void 0 ? void 0 : cachedQuery.config)
            ? mergeExpressionMaps(expressionMap, cachedQuery.config)
            : expressionMap;
        return applyQueryFilters(config, Object.assign(Object.assign({}, pagination), { search: debouncedSearch, searchPaths: searchFields, paginated: !hidePagination }));
    }, [
        cachedQuery === null || cachedQuery === void 0 ? void 0 : cachedQuery.config,
        debouncedSearch,
        filter,
        filterTarget,
        hidePagination,
        pagination,
        searchFields
    ]);
    const runQueryWithFilters = useCallback(() => {
        runQueryOrPaginated(queryId, !hidePagination, expressionMap);
    }, [expressionMap, hidePagination, queryId, runQueryOrPaginated]);
    useEffect(() => {
        runQueryWithFilters();
    }, [runQueryWithFilters]);
    // refresh on task change status
    useEffect(() => {
        return TaskEvent.handle(TaskEventType.ChangeStatus, () => {
            runQueryWithFilters();
        });
    }, [runQueryWithFilters]);
    // handle refresh on assign entity
    useEffect(() => {
        return CoreEvent.handle(AssignEntityEvent.Assign, data => {
            // only run if event was triggered from the same assignEntity id
            const componentId = assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.id;
            if (data && componentId && data.id === componentId) {
                runQueryWithFilters();
            }
        });
    }, [assignEntity === null || assignEntity === void 0 ? void 0 : assignEntity.id, runQueryWithFilters]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteTablePlugin }, props),
        React.createElement(SiteTableRenderer, { component: component, queryResult: queryResult, workflowDisabled: disabled, hidePagination: hidePagination, columns: ((_e = component.props) === null || _e === void 0 ? void 0 : _e.columns) || [], onPaginationChange: setPagination, onRowClick: (row) => siteEvent.emit('onClick', row), tableProps: Object.assign(Object.assign({}, hoverProps(component)), { className: className, size: (_f = component.props) === null || _f === void 0 ? void 0 : _f.size, padding: (_g = component.props) === null || _g === void 0 ? void 0 : _g.padding, stickyHeader: (_h = component.props) === null || _h === void 0 ? void 0 : _h.stickyHeader, onMouseOver: () => {
                    editing && !isHovering && setHoverComponent(component);
                }, onMouseOut: () => {
                    editing && !isHovering && setHoverComponent(component);
                } }) }),
        children));
};
export const SiteTableConfigurator = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j;
    const context = useSiteContext();
    const { plugins } = context;
    const { queries } = useQueryContext();
    const { component, trackChanges, move, insert, update, remove, changeType, insertChild, removeChild } = useConfigComponent(props.component);
    const filter = (_a = component.props) === null || _a === void 0 ? void 0 : _a.filter;
    const query = queries.find(query => { var _a; return query.id === ((_a = component.queryIds) === null || _a === void 0 ? void 0 : _a[0]); });
    const filterFieldOptions = (((_b = query === null || query === void 0 ? void 0 : query.config) === null || _b === void 0 ? void 0 : _b.selects) || []).map(select => select.selection);
    const handleTypeChange = (column, type) => {
        const plugin = plugins.find(p => p.type === type);
        const index = (component.children || []).findIndex(child => { var _a, _b; return ((_b = (_a = child.props) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.id) === column.id; });
        if (!type) {
            if (index > -1) {
                removeChild(index);
            }
        }
        else if (index < 0) {
            insertChild(type, plugin.name, { props: { column } });
        }
        else {
            changeType(type, `children[${index}]`);
            update(`children[${index}].props.column`, column);
            // run initConfig on after updating component type
            const component = trackChanges.get(`children[${index}]`);
            if (!(plugin === null || plugin === void 0 ? void 0 : plugin.initConfig)) {
                return;
            }
            // update props that changed to apply changes
            const componentProps = Object.assign({}, component.props);
            const updated = plugin.initConfig(component, context);
            for (const [k, value] of Object.entries(updated.props || {})) {
                const key = k;
                if (componentProps[key] !== value) {
                    update(`children[${index}].props.${key}`, value);
                }
            }
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(SiteConfigComponentQuery, { component: component }),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { autoFocus: true, fullWidth: true, size: 'small', label: 'Name', value: component.name || '', InputProps: { disableUnderline: true }, onChange: event => update('name', event.target.value) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { select: true, fullWidth: true, size: 'small', label: 'Padding', value: ((_c = component.props) === null || _c === void 0 ? void 0 : _c.padding) || '', InputProps: { disableUnderline: true }, onChange: event => update('props.padding', event.target.value) }, ['default', 'checkbox', 'none'].map((padding) => {
                var _a;
                return (React.createElement(MenuItem, { key: padding, value: padding, selected: ((_a = component.props) === null || _a === void 0 ? void 0 : _a.padding) === padding }, padding));
            }))),
        React.createElement(ConfigFieldControl, null,
            React.createElement(TextField, { select: true, fullWidth: true, size: 'small', label: 'Size', value: ((_d = component.props) === null || _d === void 0 ? void 0 : _d.size) || '', InputProps: { disableUnderline: true }, onChange: event => update('props.size', event.target.value) }, ['small', 'medium'].map((size) => {
                var _a;
                return (React.createElement(MenuItem, { key: size, value: size, selected: ((_a = component.props) === null || _a === void 0 ? void 0 : _a.size) === size }, size));
            }))),
        React.createElement(ConfigFieldControl, null,
            React.createElement(FormControlLabel, { label: 'Sticky Header', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: !!((_e = component.props) === null || _e === void 0 ? void 0 : _e.stickyHeader), onChange: () => {
                        var _a;
                        update('props.stickyHeader', !((_a = component.props) === null || _a === void 0 ? void 0 : _a.stickyHeader));
                    } }) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(FormControlLabel, { label: 'Include Pagination', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: !((_f = component.props) === null || _f === void 0 ? void 0 : _f.hidePagination), onChange: () => {
                        var _a;
                        update('props.hidePagination', !((_a = component.props) === null || _a === void 0 ? void 0 : _a.hidePagination));
                    } }) })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(FormControlLabel, { label: 'Sort with Query Params', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: !!((_g = component.props) === null || _g === void 0 ? void 0 : _g.sortWithQueryParams), onChange: () => {
                        var _a;
                        update('props.sortWithQueryParams', !((_a = component.props) === null || _a === void 0 ? void 0 : _a.sortWithQueryParams));
                    } }) })),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Search Filter Columns")),
        React.createElement(ConfigFieldControl, null,
            React.createElement(SiteQueryFieldPicker, { multiple: true, hideQueryName: true, queryId: query === null || query === void 0 ? void 0 : query.id, queryField: ((_h = component.props) === null || _h === void 0 ? void 0 : _h.searchFields) || [], options: query ? [query] : [], onChange: value => {
                    const fields = value.map(v => v.queryField);
                    update('props.searchFields', fields);
                } })),
        React.createElement(ConfigFieldControl, null,
            React.createElement(FormControlLabel, { label: 'Filter by Route Param', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: !!filter, onChange: () => update('props.filter', filter ? undefined : {}) }) })),
        filter && (React.createElement(React.Fragment, null,
            React.createElement(Box, { mt: 1 },
                React.createElement(Typography, { variant: 'overline' }, "Filter by Route Param"),
                React.createElement(Typography, { variant: 'body1', color: 'textSecondary' },
                    "The selected ",
                    React.createElement("strong", null, "route param"),
                    " will be checked against the selected ",
                    React.createElement("strong", null, "field"),
                    ".")),
            React.createElement(SiteRouteAndParamPicker, { nearestOnly: true, component: component, routeId: filter.routeId, routeParam: filter.routeParam, siteRoutePickerProps: { label: 'Route' }, onChange: value => {
                    updateRouteAndParam(value, 'props.filter.routeId', 'props.filter.routeParam').forEach(([path, value]) => update(path, value));
                } }),
            React.createElement(ConfigFieldControl, null,
                React.createElement(Autocomplete, { fullWidth: true, value: filter.field, options: filterFieldOptions, onChange: (_, value) => {
                        update('props.filter.field', value || undefined);
                    }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Select a Field', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) })))),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Columns")),
        React.createElement(ConfigSortableList, { data: ((_j = component.props) === null || _j === void 0 ? void 0 : _j.columns) || [], keyIndicator: (item) => item.id, renderItem: (item, index) => (React.createElement(SiteTableColumnConfig, { value: item, component: component, onChange: (property, value) => {
                    update(`props.columns[${index}].${property}`, value);
                    if (property === 'componentType') {
                        handleTypeChange(item, typeof value === 'string' ? value : undefined);
                    }
                } })), footer: React.createElement(SiteTableColumnConfig, { onEnterKey: (item) => {
                    var _a;
                    if (!((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns)) {
                        update('props.columns', []);
                    }
                    insert('props.columns', item);
                    handleTypeChange(item, item.componentType);
                } }), onMove: (sourceIndex, destinationIndex) => {
                move('props.columns', sourceIndex, destinationIndex);
            }, onDelete: (index) => {
                var _a, _b;
                // remove format component
                const item = (_b = (_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) === null || _b === void 0 ? void 0 : _b[index];
                if (item) {
                    handleTypeChange(item, undefined);
                }
                remove(`props.columns[${index}]`);
            } }),
        React.createElement(Box, { mt: 1 },
            React.createElement(Typography, { variant: 'overline' }, "Events")),
        React.createElement(SiteEventConfigurator, { eventTypes: ['onClick'], component: component })));
};
const StyledSiteTableColumnConfigDiv = styled.div `
  display: flex;
  width: 100%;
  align-items: center;
  .input-container {
    flex: 1;
  }
  .MuiFormHelperText-root {
    width: 0;
    min-width: 100%;
    padding-left: 8px;
    padding-right: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;
function getColumn(value) {
    return value
        ? value.componentType
            ? value
            : Object.assign(Object.assign({}, value), { componentType: 'text' })
        : { componentType: 'text' };
}
export const SiteTableColumnConfig = (props) => {
    var _a;
    const { value, component, onChange, onEnterKey } = props;
    const { queries } = useQueryContext();
    const [column, setColumn] = useState(getColumn(value));
    const configureComponent = (_a = component === null || component === void 0 ? void 0 : component.children) === null || _a === void 0 ? void 0 : _a.find(child => { var _a, _b; return ((_b = (_a = child.props) === null || _a === void 0 ? void 0 : _a.column) === null || _b === void 0 ? void 0 : _b.id) === column.id; });
    const { queryId, queryField } = (configureComponent === null || configureComponent === void 0 ? void 0 : configureComponent.props) || {};
    const query = queries.find(query => query.id === queryId);
    const handleAddItem = (event) => {
        if (!onEnterKey) {
            return;
        }
        switch (event.key) {
            case 'Enter':
                const { name = '' } = column;
                onEnterKey({ id: uuid(), name, componentType: 'text' });
                setColumn({ componentType: 'text' });
        }
    };
    const helperText = query && queryField ? `${queryField} in ${query.name}` : undefined;
    useEffect(() => {
        setColumn(getColumn(value));
    }, [value]);
    return (React.createElement(StyledSiteTableColumnConfigDiv, null,
        component && (React.createElement("div", null,
            React.createElement(NestedComponentPicker, { componentType: column.componentType, configureComponent: configureComponent, onTypeChange: type => {
                    setColumn(Object.assign(Object.assign({}, column), { componentType: type }));
                    onChange === null || onChange === void 0 ? void 0 : onChange('componentType', type);
                } },
                React.createElement(StyledConfigFieldControl, null,
                    React.createElement(SiteQueryFieldPicker, { options: queries, queryId: column.sortQueryId, queryField: column.sortQueryField, onChange: value => {
                            setColumn(Object.assign(Object.assign({}, column), { sortQueryId: value.queryId, sortQueryField: value.queryField }));
                            onChange === null || onChange === void 0 ? void 0 : onChange('sortQueryId', value.queryId);
                            onChange === null || onChange === void 0 ? void 0 : onChange('sortQueryField', value.queryField);
                        } }),
                    React.createElement(FormHelperText, null, "Select the field to sort (defaults to the displayed field if not provided)")),
                React.createElement(ConfigFieldControl, null,
                    React.createElement(ConfigTextField, { fullWidth: true, type: 'number', label: 'Column Width (px)', value: column.width || '', onChange: event => {
                            const value = event.target.value;
                            const width = value ? parseInt(value) : undefined;
                            setColumn(Object.assign(Object.assign({}, column), { width }));
                            onChange === null || onChange === void 0 ? void 0 : onChange('width', width);
                        } })),
                React.createElement(FormHelperText, null, "Set custom column width"),
                React.createElement(ConfigFieldControl, null,
                    React.createElement(FormControlLabel, { label: 'Sortable', control: React.createElement(Checkbox, { size: 'small', color: 'primary', className: 'checkbox', checked: !column.disabledSort, onChange: () => {
                                const disabledSort = !column.disabledSort;
                                setColumn(Object.assign(Object.assign({}, column), { disabledSort }));
                                onChange === null || onChange === void 0 ? void 0 : onChange('disabledSort', disabledSort);
                            } }) }))))),
        React.createElement("div", { className: 'input-container' },
            React.createElement(ConfigInput, { fullWidth: true, placeholder: 'Add Column', value: column.name || '', onKeyDown: handleAddItem, onChange: event => {
                    const { value: name } = event.target;
                    setColumn(Object.assign(Object.assign({}, column), { name }));
                    onChange === null || onChange === void 0 ? void 0 : onChange('name', name);
                } }),
            helperText && (React.createElement(FormHelperText, { title: helperText }, helperText)))));
};
export const SiteTablePlugin = {
    name: 'Site Table',
    type: 'table',
    component: SiteTable,
    configurator: SiteTableConfigurator
};
//# sourceMappingURL=SiteTablePlugin.js.map